import React, { useEffect, useRef, useState } from 'react';
import { Button, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiErrorCircle } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { UserRoles } from '../../../../../enum/UserRoles';
import { BackButton } from '../../../../../shared/components/button/BackButton';
import { IAllStates } from '../../../../../store/rootReducer';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { UserAndRolesBasicPack } from '../../../packages/components/UserAndRolesBasicPack';
import { ICompanyUser } from '../../dto/ICompanyUser';
import { UserService } from '../../service';
import { UserAndRolesCard } from './UserAndRolesCard';
import UserAndRolesModal from './UserAndRolesModal';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import UserAndRolesFilter from './UserAndRolesFilter';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { ICompanyInfoData, ICompanyUsersFilter } from '../../dto/ICompany';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import InfiniteScroll from 'react-infinite-scroll-component';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { getTruncatedData } from '../../../../../utils/helpers/getTruncatedData';
import UserManageGroupsModal from './UserManageGroupsModal';
import { UserAndRolesCardSkeleton, UserAndRolesHeaderSkeleton } from './UserAndRolesCardSkeleton';

const SKELETON_COUNT = 8;

function UserAndRoles() {
    const userType = useSelector((reduxState: IAllStates) => reduxState.auth?.user?.userType);
    const teamCollaboration = useSelector(
        (reduxState: IAllStates) => reduxState.auth?.user?.company?.package?.teamCollaboration
    );
    const isSupplier = useSelector((reduxState: IAllStates) => reduxState.auth?.user?.isSupplier);
    const [showCreateNewUserModal, setShowCreateNewUserModal] = useState(false);
    const [showManageGroupsModal, setShowManageGroupsModal] = useState(false);
    const [usersList, setUsersList] = useState<ICompanyUser[]>([]);
    const [info, setInfo] = useState<ICompanyInfoData>();
    const [count, setCount] = useState(0);
    const [updateList, setUpdateList] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<ICompanyUser>();
    const [isUsersLoading, setIsUsersLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();
    const { id } = useParams<keyof IParams>();
    const [filterParams, setFilterParams] = useState<ICompanyUsersFilter>({
        page: 1,
        perPage: 20,
        displayName: '',
        position: '',
        isEnabled: true,
    });
    const { t } = useTranslation();
    const isMangeGroupsVisible = !!id && isSupplier;
    const maximumEmployees = info?.maximumEmployees ?? 0;
    const isMaximumUsersReached = !!info && (info.activeCount ?? 0) >= maximumEmployees;
    const displayName = info?.company?.displayName ?? '-';
    const { truncatedName, isTruncated } = getTruncatedData(displayName, 23);

    useEffect(() => {
        const getUsersFunction = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles)
            ? UserService.getAgencyCompanyUsersV2
            : UserService.getCompanyUsersV2;

        // Skelet samo kad se promijeni filter ili klijent, ne za load more (perPage) i osvježavanja nakon akcija
        const queryKey = `${id}-${JSON.stringify({ ...filterParams, perPage: undefined })}`;
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsUsersLoading(true);
        }

        let isCurrent = true;
        getUsersFunction({ ...filterParams, clientId: id })
            .then(data => {
                if (!isCurrent) return;
                setUsersList(data?.data);
                setInfo(data?.info);
                setCount(data?.count);
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsUsersLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [updateList, filterParams, id]);

    function disableUser(userId: string) {
        const disableUserFunction = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles)
            ? UserService.disableAgencyUser
            : UserService.disableCompanyUser;

        disableUserFunction(userId)
            .then(() => {
                SuccessToast(t('general.toasts.successUpdated'));
                if (info?.inactiveCount === 1 && !filterParams?.isEnabled) {
                    setFilterParams(prevState => ({ ...prevState, isEnabled: true }));
                } else {
                    setUpdateList(prevState => !prevState);
                }
            })
            .catch(error => ErrorToast(error));
    }

    const deleteUser = () => {
        UserService.deleteCompanyUser(selectedUser?.id ?? '')
            .then(() => {
                if (info?.inactiveCount === 1 && !filterParams?.isEnabled) {
                    setFilterParams(prevState => ({ ...prevState, isEnabled: true }));
                } else {
                    setUpdateList(prevState => !prevState);
                }
                SuccessToast(t('general.toasts.successRemovedUser'));
            })
            .catch(error => ErrorToast(error));
    };

    const loadMoreData = () => {
        setFilterParams(prev => ({ ...prev, perPage: prev.perPage + 8 }));
    };

    const handleShowCreateUserModal = () => setShowCreateNewUserModal(true);

    const handleCloseCreateUserModal = () => {
        setShowCreateNewUserModal(false);
        setSelectedUser(undefined);
    };

    const handleCloseManageGroupsModal = () => {
        setShowManageGroupsModal(false);
        setSelectedUser(undefined);
    };

    const deleteUserAction = (companyUser?: ICompanyUser) => {
        setShowConfirmModal(true);
        setSelectedUser(companyUser);
    };

    const manageGroupsAction = (companyUser?: ICompanyUser) => {
        setShowManageGroupsModal(true);
        setSelectedUser(companyUser);
    };

    const closeConfirmModalAction = () => {
        setSelectedUser(undefined);
        setShowConfirmModal(false);
    };

    const editUser = (companyUser?: ICompanyUser) => {
        setSelectedUser(companyUser);
        setShowCreateNewUserModal(true);
    };

    return (
        <div className="container">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <BackButton />
                    <div className="d-flex flex-column">
                        <h5 className="mb-0">
                            {!!id ? t('brand.account.client_team_collab') : t('brand.account.users_and_roles')}
                        </h5>
                        <span className="text-muted">{t('brand.account.users_and_roles_desc')}</span>
                    </div>
                </div>
                <div className="mb-2 mb-sm-0">
                    {!info ? (
                        <UserAndRolesHeaderSkeleton />
                    ) : (
                        <div className="d-flex align-items-center justify-content-end">
                            <div className="text-end mr-2">
                                <TooltipOverlay
                                    className="justify-content-end"
                                    content={isTruncated ? displayName : ''}
                                >
                                    <h6 className="mb-0">{truncatedName}</h6>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted">
                                    {t('brand.account.users_and_roles_desc')}
                                    {filterParams.isEnabled ? (
                                        <span className="text-primary font-weight-bold ml-1">
                                            {count}/{maximumEmployees}
                                        </span>
                                    ) : (
                                        <span className="text-primary font-weight-bold ml-1">{count}</span>
                                    )}
                                </p>
                            </div>
                            <div className="d-md-flex d-none align-items-center">
                                <CustomImageHandler
                                    photoPath={info?.company?.profilePhoto?.path ?? ''}
                                    altTag="User Profile"
                                    classes="middle-rounded-logo"
                                    name={info?.company?.displayName}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {!teamCollaboration && UserRoles.BRAND_MASTER === userType ? (
                <UserAndRolesBasicPack />
            ) : (
                <>
                    <div className="mb-4 d-md-flex align-items-center justify-content-between">
                        <UserAndRolesFilter
                            setFilterParams={setFilterParams}
                            filters={filterParams}
                            isBrand={[UserRoles.BRAND_MASTER, UserRoles.BRAND].includes(userType)}
                            isFilterLoading={isUsersLoading}
                        />
                        <div className="d-md-flex align-items-center" style={{ gap: 8 }}>
                            {(info?.inactiveCount ?? 0) > 0 && (
                                <Button
                                    className="w-100 w-md-auto mb-3 mb-md-0"
                                    variant="outline-primary"
                                    disabled={isUsersLoading}
                                    onClick={() =>
                                        setFilterParams(prevState => ({
                                            ...prevState,
                                            isEnabled: !prevState.isEnabled,
                                        }))
                                    }
                                >
                                    {filterParams.isEnabled
                                        ? t('general.button.seeInactive')
                                        : t('general.button.seeActive')}
                                </Button>
                            )}

                            <Button
                                className="w-100 w-md-auto"
                                disabled={!info || isMaximumUsersReached}
                                onClick={handleShowCreateUserModal}
                            >
                                {t('general.button.addTeamMember')}
                            </Button>
                            {isMaximumUsersReached && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="maximum-users-reached">
                                            <span>{t('brand.package.maximumUsersReached')}</span>
                                        </Tooltip>
                                    }
                                >
                                    <span>
                                        <BiErrorCircle size={20} className="text-danger cursor-pointer" />
                                    </span>
                                </OverlayTrigger>
                            )}
                        </div>
                    </div>
                    {isUsersLoading ? (
                        <Row>
                            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                <UserAndRolesCardSkeleton key={i} />
                            ))}
                        </Row>
                    ) : !!usersList?.length ? (
                        <InfiniteScroll
                            dataLength={usersList.length ?? 0}
                            next={loadMoreData}
                            endMessage={
                                <p
                                    className={`text-center ${
                                        (usersList?.length ?? 0) !== count || count < 20 ? 'd-none' : ''
                                    }`}
                                >
                                    <b>You have seen all!</b>
                                </p>
                            }
                            hasMore={(usersList?.length ?? 0) < count}
                            loader={
                                <Row>
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <UserAndRolesCardSkeleton key={i} />
                                    ))}
                                </Row>
                            }
                            scrollableTarget="scrollableDiv"
                        >
                            <Row>
                                {usersList?.map(user => (
                                    <UserAndRolesCard
                                        key={user?.id}
                                        companyUser={user}
                                        disableUser={disableUser}
                                        deleteUserAction={deleteUserAction}
                                        editUser={editUser}
                                        manageGroupsAction={manageGroupsAction}
                                        isMangeGroupsVisible={isMangeGroupsVisible}
                                    />
                                ))}
                            </Row>
                        </InfiniteScroll>
                    ) : (
                        <EmptyState />
                    )}
                </>
            )}
            {!!id && (
                <UserManageGroupsModal
                    show={showManageGroupsModal}
                    closeModal={handleCloseManageGroupsModal}
                    userId={selectedUser?.id}
                    clientId={id}
                    setUpdateList={setUpdateList}
                />
            )}
            <UserAndRolesModal
                show={showCreateNewUserModal}
                closeModal={handleCloseCreateUserModal}
                companyUser={selectedUser}
                updateList={setUpdateList}
                clientId={id}
            />
            <ConfirmationModal
                title={t('brand.account.delete_user_title')}
                description={t('brand.account.delete_user_desc')}
                action={deleteUser}
                show={showConfirmModal}
                closeModal={closeConfirmModalAction}
            />
        </div>
    );
}

export default UserAndRoles;
